import React, { FC, useState, useEffect, useMemo, useCallback, useRef } from "react";
import { periods, transferFieldConfig, TransferMetricKey, transferMetricOptions } from "../statisticsConfig";
import { buildGroupedData, formatTransferMetricValue, formatCompact, formatCurrency, formatNumber } from "../statisticsUtils";
import DateInputGradient from "../../../atoms/DateInputGradient/DateInputGradient";
import Select from "../../../atoms/Select/Select";
import MiniChart from "../../../atoms/MiniChart/MiniChart";
import { IDailyDeal } from "../TransferSection";

export const transferMetricOptionsList = [
    { value: "rub_amount", name: "сумма в рублях" },
    { value: "purchase_amount", name: "сумма закупки" },
    { value: "profit_rub", name: "прибыль в USDT" },
    // { value: "transfer_amount", name: "сумма перевода" },
    { value: "send_amount", name: "сумма отправки" }
];

interface TransferOverallTabProps {
    stats: any;
    dailyDeals: IDailyDeal[] | null;
    linkTo: string;
    colorConfig: { textColor: string; color: string };
    transferMetric: TransferMetricKey;
    setTransferMetric: (v: TransferMetricKey) => void;
    overallDateFrom: string;
    overallDateTo: string;
    setOverallDateFrom: (v: string) => void;
    setOverallDateTo: (v: string) => void;
    onReset: () => void;

    chartPeriod: "month" | "year";
    setChartPeriod: (v: "month" | "year") => void;
    chartYear: number;
    setChartYear: (v: number) => void;
    chartMonth: number;
    setChartMonth: (v: number) => void;

    yearlyDeals: IDailyDeal[] | null;
    availableYears: number[];
}

const DEFAULT_PURCHASE_AMOUNT_CURRENCY = "USDT";
const DEFAULT_SEND_CURRENCY = "USDT";

const CHART_BLUE_COLOR = "#277DFF";
const CHART_GREEN_COLOR = "#27AE60";

const MONTH_OPTIONS = [
    { value: "0", name: "Январь" },
    { value: "1", name: "Февраль" },
    { value: "2", name: "Март" },
    { value: "3", name: "Апрель" },
    { value: "4", name: "Май" },
    { value: "5", name: "Июнь" },
    { value: "6", name: "Июль" },
    { value: "7", name: "Август" },
    { value: "8", name: "Сентябрь" },
    { value: "9", name: "Октябрь" },
    { value: "10", name: "Ноябрь" },
    { value: "11", name: "Декабрь" }
];

const getAllTimeKey = (overall: any, metric: TransferMetricKey, preferredKey: any) => {
    const candidates = [String(preferredKey ?? ""), "all_time", "all", "alltime", "total", "lifetime"].filter(Boolean);

    for (const k of candidates) {
        const v = overall?.[k]?.[metric];
        if (v !== undefined && v !== null) return k;
    }

    return String(preferredKey ?? "");
};

const TransferOverallTab: FC<TransferOverallTabProps> = ({
                                                             stats,
                                                             dailyDeals,
                                                             linkTo,
                                                             colorConfig,
                                                             transferMetric,
                                                             setTransferMetric,
                                                             overallDateFrom,
                                                             overallDateTo,
                                                             setOverallDateFrom,
                                                             setOverallDateTo,
                                                             onReset,
                                                             chartPeriod,
                                                             setChartPeriod,
                                                             chartYear,
                                                             setChartYear,
                                                             chartMonth,
                                                             setChartMonth,
                                                             availableYears,
                                                             yearlyDeals
                                                         }) => {
    const [activeCityGroup, setActiveCityGroup] = useState<string | null>(null);
    const [activeCurrencyGroup, setActiveCurrencyGroup] = useState<string | null>(null);

    const [chartMode, setChartMode] = useState<"volume" | "profit">("volume");

    const chartOverallRef = useRef<{ volume: any | null; profit: any | null }>({ volume: null, profit: null });

    useEffect(() => {
        setActiveCityGroup(null);
        setActiveCurrencyGroup(null);
    }, [transferMetric]);

    const chartDisplayMetric = useMemo<TransferMetricKey>(() => {
        return chartMode === "profit" ? "profit_rub" : "send_amount";
    }, [chartMode]);

    useEffect(() => {
        const overall = stats?.transfer?.overall;
        if (!overall) return;

        const hasMetric = (metric: TransferMetricKey) =>
            Object.values(overall).some((v: any) => v && typeof v === "object" && v[metric] !== undefined && v[metric] !== null);

        if (!chartOverallRef.current.volume && hasMetric("send_amount")) chartOverallRef.current.volume = overall;
        if (!chartOverallRef.current.profit && hasMetric("profit_rub")) chartOverallRef.current.profit = overall;
    }, [stats]);

    const yearOptions = useMemo(() => {
        const yearsToUse = availableYears && availableYears.length > 0 ? availableYears : [new Date().getFullYear()];

        return yearsToUse.map(year => ({
            value: String(year),
            name: String(year)
        }));
    }, [availableYears]);

    const { mainPeriods, allTimePeriod } = useMemo(() => {
        const norm = (v: any) => String(v ?? "").toLowerCase();

        const all =
            periods.find((p: any) => {
                const k = norm(p.key);
                const l = norm(p.label);
                return ["all", "all_time", "alltime", "total", "lifetime"].includes(k) || /все|всё|all\s*time|lifetime/i.test(l);
            }) || null;

        const pick = (rx: RegExp, keyFallback?: string) => {
            const byKey = keyFallback ? periods.find((p: any) => norm(p.key) === norm(keyFallback)) : null;
            if (byKey && (!all || byKey.key !== all.key)) return byKey;

            const byLabel = periods.find((p: any) => rx.test(norm(p.label)) && (!all || p.key !== all.key));
            return byLabel || null;
        };

        const desired = [pick(/день|day/i, "day"), pick(/недел|week/i, "week"), pick(/месяц|month/i, "month"), pick(/год|year/i, "year")].filter(
            Boolean
        ) as any[];

        const dedup = Array.from(new Map(desired.map((p: any) => [String(p.key), p])).values());

        const fallbackMain = periods.filter((p: any) => !all || p.key !== all.key);

        return {
            mainPeriods: dedup.length ? dedup : fallbackMain,
            allTimePeriod: all
        };
    }, []);

    // Предвычисляем send_amount по валютам для использования в renderTransferByCurrencyDefault
    const sendAmountByCurrencyMap = useMemo(() => {
        const byCurrency = stats?.transfer?.byCurrency;
        if (!byCurrency) return new Map<string, any>();
        return new Map(
            buildGroupedData(byCurrency, "purchase_currency", "send_amount").map((d: any) => [d.groupValue, d])
        );
    }, [stats]);

    const chartData = useMemo(() => {
        if (!dailyDeals || dailyDeals.length === 0) {
            return { values: [], dates: [], items: [], tooltipDates: [] };
        }

        const sorted = [...dailyDeals].sort((a, b) => a.date - b.date);

        return {
            values: sorted.map(i => (chartMode === "volume" ? i.volume : i.profit)),
            dates: sorted.map(i => {
                const d = new Date(Number(i.date));
                return chartPeriod === "year" ? d.toLocaleDateString("ru-RU", { month: "short" }) : d.toLocaleDateString("ru-RU", { day: "numeric" });
            }),
            tooltipDates: sorted.map(i => {
                const d = new Date(Number(i.date));
                return chartPeriod === "year"
                    ? d.toLocaleDateString("ru-RU", { month: "long", year: "numeric" })
                    : d.toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
            }),
            items: sorted
        };
    }, [dailyDeals, chartPeriod, chartMode]);

    // Сумма по выбранному периоду из dailyDeals (для плашки месяца)
    const selectedPeriodTotal = useMemo(() => {
        if (!chartData.values.length) return null;
        return chartData.values.reduce((a, b) => a + b, 0);
    }, [chartData.values]);

    // Годовой итог — считается из yearlyDeals (отдельный запрос за полный год)
    const selectedYearTotal = useMemo(() => {
        if (!yearlyDeals || yearlyDeals.length === 0) return null;
        return yearlyDeals.reduce((acc, item) => acc + (chartMode === "volume" ? item.volume : item.profit), 0);
    }, [yearlyDeals, chartMode]);

    const renderTransferTooltip = useCallback(
        (index: number) => {
            const item = chartData.items[index];
            if (!item) return [];

            return [
                `Сделок: ${item.count} `,
                `Объем: ${formatCompact(item.volume)} USDT`,
                `Профит: ${formatCurrency(item.profit, "USDT")}`
            ];
        },
        [chartData.items]
    );

    const renderTransferOverallDefault = () => {
        const overallRaw = stats?.transfer?.overall;

        const hasMetricInOverall = (obj: any, metric: TransferMetricKey) =>
            Object.values(obj || {}).some((v: any) => v && typeof v === "object" && v[metric] !== undefined && v[metric] !== null);

        const preferred = chartMode === "profit" ? chartOverallRef.current.profit : chartOverallRef.current.volume;
        const overall = preferred && hasMetricInOverall(preferred, chartDisplayMetric) ? preferred : overallRaw;

        if (!overall) return null;

        const allTimeKey = allTimePeriod ? getAllTimeKey(overall, chartDisplayMetric, allTimePeriod.key) : "";

        return (
            <div className="statistics-items__item">
                <div className={"statistics-items__inner"}>
                    <div className={`statistics-items__item-charts`}>
                        <div className="statistics-items__chart-controls">
                            <div className="statistics-items__chart-group">
                                <button
                                    type="button"
                                    className={
                                        chartMode === "volume"
                                            ? "statistics-items__transfer-tab statistics-items__transfer-tab--active"
                                            : "statistics-items__transfer-tab"
                                    }
                                    onClick={() => setChartMode("volume")}
                                >
                                    Объем
                                </button>
                                <button
                                    type="button"
                                    className={
                                        chartMode === "profit"
                                            ? "statistics-items__transfer-tab statistics-items__transfer-tab--active"
                                            : "statistics-items__transfer-tab"
                                    }
                                    onClick={() => setChartMode("profit")}
                                >
                                    Профит
                                </button>
                            </div>

                            <div className="statistics-items__chart-group">
                                <button
                                    type="button"
                                    className={
                                        chartPeriod === "month"
                                            ? "statistics-items__transfer-tab statistics-items__transfer-tab--active"
                                            : "statistics-items__transfer-tab"
                                    }
                                    onClick={() => setChartPeriod("month")}
                                >
                                    По дням
                                </button>

                                <button
                                    type="button"
                                    className={
                                        chartPeriod === "year"
                                            ? "statistics-items__transfer-tab statistics-items__transfer-tab--active"
                                            : "statistics-items__transfer-tab"
                                    }
                                    onClick={() => setChartPeriod("year")}
                                >
                                    По месяцам
                                </button>

                                <Select
                                    className="select-tab"
                                    name="chartYear"
                                    items={yearOptions}
                                    defaultValue={String(chartYear)}
                                    onChange={(v: string) => setChartYear(Number(v))}
                                    placeholder="Год"
                                />

                                {chartPeriod === "month" && (
                                    <Select
                                        className="select-tab"
                                        name="chartMonth"
                                        items={MONTH_OPTIONS}
                                        defaultValue={String(chartMonth)}
                                        onChange={(v: string) => setChartMonth(Number(v))}
                                        placeholder="Месяц"
                                    />
                                )}
                            </div>
                        </div>

                        <MiniChart
                            data={chartData.values}
                            labels={chartData.dates}
                            tooltipLabels={chartData.tooltipDates}
                            colorHex={chartMode === "profit" ? CHART_GREEN_COLOR : CHART_BLUE_COLOR}
                            renderTooltip={renderTransferTooltip}
                        />
                    </div>

                    <div className={"statistics-items__details"}>
                        <div className="statistics-items__item-content-counter-total statistics-items__item-content-counter-total--row">
                            <div className="statistics-items__periods">
                                <div className="statistics-items__periods-grid">
                                    {mainPeriods.map((p: any) => {
                                        const overallDisplayCurrency = chartMode === "profit" ? "USDT" : DEFAULT_SEND_CURRENCY;

                                        const isSelectedMonth = chartPeriod === "month" && p.key === "month";
                                        const isYearBadge = p.key === "year";

                                        let v: number;
                                        let periodLabel: string;

                                        if (isSelectedMonth && selectedPeriodTotal !== null) {
                                            v = selectedPeriodTotal;
                                            periodLabel = `${MONTH_OPTIONS[chartMonth]?.name} ${chartYear}`;
                                        } else if (isYearBadge && selectedYearTotal !== null) {
                                            v = selectedYearTotal;
                                            periodLabel = `${chartYear} г.`;
                                        } else {
                                            v = Number(overall?.[p.key]?.[chartDisplayMetric] ?? 0);
                                            periodLabel = isYearBadge ? `${chartYear} г.` : p.label;
                                        }

                                        return (
                                            <div key={`transfer-overall-${chartDisplayMetric}-${p.key}`}>
                                                <div className={'statistics-items__periods-price'}>
                                                    {formatNumber(v)}
                                                </div>
                                                <span>{periodLabel} ({overallDisplayCurrency})</span>
                                            </div>
                                        );
                                    })}
                                </div>

                                {allTimePeriod && allTimeKey && (
                                    <div className="statistics-items__periods-all">
                                        <div key={`transfer-overall-${chartDisplayMetric}-${allTimeKey}`}>
                                            <div className={'statistics-items__periods-price'}>
                                                {formatNumber(Number(overall?.[allTimeKey]?.[chartDisplayMetric] ?? 0))}
                                            </div>
                                            <span>{allTimePeriod.label} ({chartMode === "profit" ? "USDT" : DEFAULT_SEND_CURRENCY})</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderTransferByCityDefault = (metricKey: TransferMetricKey) => {
        const byCity = stats?.transfer?.byCity;
        if (!byCity) return null;

        const data = buildGroupedData(byCity, "city", metricKey);
        if (!data.length) return null;

        // *** ИЗМЕНЕНИЕ: Для Online-городов при rub_amount показываем purchase_amount ***
        const purchaseDataMap = new Map<string, any>();
        if (metricKey === "rub_amount") {
            const purchaseData = buildGroupedData(byCity, "city", "purchase_amount");
            purchaseData.forEach((d: any) => purchaseDataMap.set(d.groupValue, d));
        }

        const currentActiveGroup =
            activeCityGroup && data.find((d: any) => d.groupValue === activeCityGroup) ? activeCityGroup : data[0].groupValue;

        const activeItem = data.find((d: any) => d.groupValue === currentActiveGroup);
        const config = transferFieldConfig[metricKey];
        const displayCurrency = metricKey === "rub_amount" ? "руб." : DEFAULT_SEND_CURRENCY;

        // Online-города всегда выводим в USDT
        const isOnlineCity = (cityName: string) => /online/i.test(cityName);

        const formatCityValue = (cityName: string, value: number) => {
            if (isOnlineCity(cityName)) {
                return formatCurrency(value, DEFAULT_SEND_CURRENCY);
            }
            return formatTransferMetricValue(
                metricKey,
                value,
                config.isMoney,
                DEFAULT_PURCHASE_AMOUNT_CURRENCY,
                DEFAULT_SEND_CURRENCY,
                DEFAULT_SEND_CURRENCY
            );
        };

        // *** ИЗМЕНЕНИЕ: Для Online-городов при rub_amount подменяем activeItem на данные purchase_amount ***
        const useAltActive = metricKey === "rub_amount" && activeItem && isOnlineCity(activeItem.groupValue);
        const effectiveActiveItem = useAltActive ? purchaseDataMap.get(activeItem!.groupValue) || activeItem : activeItem;

        return (
            <div className="statistics-items__item">
                <div className={"statistics-items__inner"}>
                    <div className="statistics-items__list-selector">
                        {data.map((item: any) => {
                            // *** ИЗМЕНЕНИЕ: Для Online-городов при rub_amount берём значения из purchase_amount ***
                            const useAlt = metricKey === "rub_amount" && isOnlineCity(item.groupValue);
                            const effectiveItem = useAlt ? purchaseDataMap.get(item.groupValue) || item : item;
                            const mainValue = effectiveItem.values["month"] || effectiveItem.values["day"] || 0;
                            const isActive = item.groupValue === currentActiveGroup;

                            return (
                                <div
                                    key={`list-city-${metricKey}-${item.groupValue}`}
                                    onClick={() => setActiveCityGroup(item.groupValue)}
                                    className={
                                        isActive
                                            ? "statistics-items__list-selector-item statistics-items__list-selector-item--active"
                                            : "statistics-items__list-selector-item"
                                    }
                                >
                                    <div className="statistics-items__list-selector-counter-main" style={{ marginBottom: 0 }}>
                                        <span>
                                            {formatCityValue(item.groupValue, mainValue)}
                                        </span>
                                        <span>
                                            <b>за месяц →</b> {item.groupValue}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="statistics-items__details">
                        {/* *** ИЗМЕНЕНИЕ: Используем effectiveActiveItem вместо activeItem для значений *** */}
                        {effectiveActiveItem && (
                            <div className="statistics-items__item-content-counter ">
                                <div className="statistics-items__item-content-counter-total statistics-items__item-content-counter-total--row">
                                    <div className="statistics-items__periods">
                                        <div className="statistics-items__periods-grid">
                                            {mainPeriods.map((p: any) => (
                                                <div key={`transfer-city-detail-${metricKey}-${effectiveActiveItem.groupValue}-${p.key}`}>
                                                    <div className={'statistics-items__periods-price'}>
                                                        {formatNumber(effectiveActiveItem.values[p.key] || 0)}
                                                    </div>
                                                    <span>{p.label} ({isOnlineCity(effectiveActiveItem.groupValue) ? "USDT" : displayCurrency})</span>
                                                </div>
                                            ))}
                                        </div>

                                        {allTimePeriod && (
                                            <div className="statistics-items__periods-all">
                                                <div key={`transfer-city-detail-${metricKey}-${effectiveActiveItem.groupValue}-${allTimePeriod.key}`}>
                                                    <div className={'statistics-items__periods-price'}>
                                                        {formatNumber(effectiveActiveItem.values[allTimePeriod.key] || 0)}
                                                    </div>
                                                    <span>{allTimePeriod.label} ({isOnlineCity(effectiveActiveItem.groupValue) ? "USDT" : displayCurrency})</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderTransferByCurrencyDefault = (metricKey: TransferMetricKey) => {
        const byCurrency = stats?.transfer?.byCurrency;
        if (!byCurrency) return null;

        const data = buildGroupedData(byCurrency, "purchase_currency", metricKey);
        if (!data.length) return null;

        const currentActiveGroup =
            activeCurrencyGroup && data.find((d: any) => d.groupValue === activeCurrencyGroup) ? activeCurrencyGroup : data[0].groupValue;

        const activeItem = data.find((d: any) => d.groupValue === currentActiveGroup);
        const config = transferFieldConfig[metricKey];
        const sendConfig = transferFieldConfig["send_amount"];

        return (
            <div className="statistics-items__item">
                <div className={"statistics-items__inner"}>
                    <div className="statistics-items__list-selector statistics-items__list-selector--small">
                        {data.map((item: any) => {
                            const mainValue = item.values["month"] || item.values["day"] || 0;
                            const transferCur = metricKey === "send_amount" ? DEFAULT_SEND_CURRENCY : item.groupValue;
                            const isActive = item.groupValue === currentActiveGroup;

                            // Данные суммы отправки для той же валюты
                            const sendData = sendAmountByCurrencyMap.get(item.groupValue);
                            const sendMainValue = sendData?.values["month"] || sendData?.values["day"] || 0;

                            return (
                                <div key={`list-cur-${metricKey}-${item.groupValue}`} className={'statistics-items__list-selector-group'}>
                                    <div

                                        onClick={() => setActiveCurrencyGroup(item.groupValue)}
                                        className={
                                            isActive
                                                ? "statistics-items__list-selector-item statistics-items__list-selector-item--active"
                                                : "statistics-items__list-selector-item"
                                        }
                                    >
                                        {/* Основная метрика */}
                                        <div className="statistics-items__list-selector-counter-main" style={{ marginBottom: 0 }}>
                                        <span>
                                            {formatTransferMetricValue(
                                                metricKey,
                                                mainValue,
                                                config.isMoney,
                                                DEFAULT_PURCHASE_AMOUNT_CURRENCY,
                                                transferCur,
                                                DEFAULT_SEND_CURRENCY
                                            )}
                                        </span>
                                            <span>
                                            <b>за месяц →</b> {item.groupValue}
                                        </span>
                                        </div>

                                    </div>
                                    <div
                                        onClick={() => setActiveCurrencyGroup(item.groupValue)}
                                        className={
                                            isActive
                                                ? "statistics-items__list-selector-item statistics-items__list-selector-item--active"
                                                : "statistics-items__list-selector-item"
                                        }
                                    >

                                        {/* Сумма отправки */}
                                        <div className="statistics-items__list-selector-counter-main" style={{ marginBottom: 0, marginTop: 4 }}>
                                        <span>
                                            {formatTransferMetricValue(
                                                "send_amount",
                                                sendMainValue,
                                                sendConfig.isMoney,
                                                DEFAULT_PURCHASE_AMOUNT_CURRENCY,
                                                DEFAULT_SEND_CURRENCY,
                                                DEFAULT_SEND_CURRENCY
                                            )}
                                        </span>
                                            <span>
                                            <b>отправка →</b> {item.groupValue}
                                        </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="statistics-items__details">
                        {activeItem &&
                            (() => {
                                const transferCur = metricKey === "send_amount" ? DEFAULT_SEND_CURRENCY : activeItem.groupValue;
                                const sendActiveItem = sendAmountByCurrencyMap.get(activeItem.groupValue);
                                const displayCurrency = metricKey === "rub_amount" ? "руб." : DEFAULT_SEND_CURRENCY;

                                return (
                                    <div className="statistics-items__item-content-counter">
                                        <div className="statistics-items__item-content-counter-total statistics-items__item-content-counter-total--row">
                                            <div className="statistics-items__periods">
                                                <div className="statistics-items__periods-grid">
                                                    {mainPeriods.map((p: any) => (
                                                        <div key={`transfer-currency-detail-${metricKey}-${activeItem.groupValue}-${p.key}`}>
                                                            <div className={'statistics-items__periods-price'}>
                                                                {formatNumber(activeItem.values[p.key] || 0)}
                                                            </div>
                                                            <span>{p.label} ({displayCurrency})</span>
                                                        </div>
                                                    ))}
                                                </div>

                                                {allTimePeriod && (
                                                    <div className="statistics-items__periods-all">
                                                        <div key={`transfer-currency-detail-${metricKey}-${activeItem.groupValue}-${allTimePeriod.key}`}>
                                                            <div className={'statistics-items__periods-price'}>
                                                                {formatNumber(activeItem.values[allTimePeriod.key] || 0)}
                                                            </div>
                                                            <span>{allTimePeriod.label} ({displayCurrency})</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                    </div>
                                );
                            })()}
                    </div>
                </div>
            </div>
        );
    };

    const renderTransferOverallRange = (metricKey: TransferMetricKey) => {
        const overall = stats?.transfer?.range?.overall;
        if (!overall) return null;

        const config = transferFieldConfig[metricKey];
        const v = overall?.[metricKey] || 0;

        return (
            <div className="statistics-items__item">
                <div className="statistics-items__item-title">
                    {config.overallTitle} (за период)
                    {metricKey === "purchase_amount" ? ` (${DEFAULT_PURCHASE_AMOUNT_CURRENCY})` : ""}
                    {metricKey === "transfer_amount" ? ` (${DEFAULT_SEND_CURRENCY})` : ""}
                    {metricKey === "send_amount" ? ` (${DEFAULT_SEND_CURRENCY})` : ""}
                </div>
                <div className="statistics-items__item-list">
                    <div className="statistics-items__item-content-counter">
                        <div className="statistics-items__item-content-counter-main">
                            <span>
                                {formatTransferMetricValue(
                                    metricKey,
                                    v,
                                    config.isMoney,
                                    DEFAULT_PURCHASE_AMOUNT_CURRENCY,
                                    DEFAULT_SEND_CURRENCY,
                                    DEFAULT_SEND_CURRENCY
                                )}
                            </span>
                            <span>
                                <b>период →</b> {overallDateFrom || "…"} — {overallDateTo || "…"}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderTransferByCityRange = (metricKey: TransferMetricKey) => {
        const list = stats?.transfer?.range?.byCity || [];
        if (!Array.isArray(list) || !list.length) return null;
        const config = transferFieldConfig[metricKey];

        const currentActiveGroup = activeCityGroup && list.find((d: any) => d.city === activeCityGroup) ? activeCityGroup : list[0].city;

        return (
            <div className="statistics-items__item">
                <div className="statistics-items__item-title">
                    {config.cityTitle} (за период)
                    {metricKey === "purchase_amount" ? ` (${DEFAULT_PURCHASE_AMOUNT_CURRENCY})` : ""}
                    {metricKey === "transfer_amount" ? ` (${DEFAULT_SEND_CURRENCY})` : ""}
                    {metricKey === "send_amount" ? ` (${DEFAULT_SEND_CURRENCY})` : ""}
                </div>

                <div className={"statistics-items__inner"}>
                    <div className="statistics-items__list-selector">
                        {list.map((item: any) => {
                            const city = item?.city || "Город не указан";
                            const v = item?.[metricKey] || 0;
                            const isActive = city === currentActiveGroup;

                            return (
                                <div
                                    key={`range-city-select-${metricKey}-${city}`}
                                    onClick={() => setActiveCityGroup(city)}
                                    className={
                                        isActive
                                            ? "statistics-items__list-selector-item statistics-items__list-selector-item--active"
                                            : "statistics-items__list-selector-item"
                                    }
                                >
                                    <div className="statistics-items__list-selector-counter-main" style={{ marginBottom: 0 }}>
                                        <span>
                                            {formatTransferMetricValue(
                                                metricKey,
                                                v,
                                                config.isMoney,
                                                DEFAULT_PURCHASE_AMOUNT_CURRENCY,
                                                DEFAULT_SEND_CURRENCY,
                                                DEFAULT_SEND_CURRENCY
                                            )}
                                        </span>
                                        <span>
                                            <b>за период →</b> {city}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    const renderTransferByCurrencyRange = (metricKey: TransferMetricKey) => {
        const list = stats?.transfer?.range?.byCurrency || [];
        if (!Array.isArray(list) || !list.length) return null;
        const config = transferFieldConfig[metricKey];

        const currentActiveGroup =
            activeCurrencyGroup && list.find((d: any) => d.purchase_currency === activeCurrencyGroup)
                ? activeCurrencyGroup
                : list[0].purchase_currency;

        return (
            <div className="statistics-items__item">
                <div className="statistics-items__item-title">
                    {config.currencyTitle} (за период)
                    {metricKey === "purchase_amount" ? ` (${DEFAULT_PURCHASE_AMOUNT_CURRENCY})` : ""}
                    {metricKey === "send_amount" ? ` (${DEFAULT_SEND_CURRENCY})` : ""}
                </div>

                <div className={"statistics-items__inner"}>
                    <div className="statistics-items__list-selector statistics-items__list-selector--small">
                        {list.map((item: any) => {
                            const cur = item?.purchase_currency || "Валюта не указана";
                            const v = item?.[metricKey] || 0;
                            const transferCur = metricKey === "send_amount" ? DEFAULT_SEND_CURRENCY : cur;
                            const isActive = cur === currentActiveGroup;

                            return (
                                <div
                                    key={`range-cur-select-${metricKey}-${cur}`}
                                    onClick={() => setActiveCurrencyGroup(cur)}
                                    className={
                                        isActive
                                            ? "statistics-items__list-selector-item statistics-items__list-selector-item--active"
                                            : "statistics-items__list-selector-item"
                                    }
                                >
                                    <div className="statistics-items__list-selector-counter-main" style={{ marginBottom: 0 }}>
                                        <span>{formatTransferMetricValue(metricKey, v, config.isMoney, DEFAULT_PURCHASE_AMOUNT_CURRENCY, transferCur)}</span>
                                        <span>
                                            <b>за период →</b> {cur}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    const isOverallRangeMode = !!(overallDateFrom || overallDateTo) && !!stats?.transfer?.range;

    return (
        <>
            <div className="statistics-items__filter">
                <div className="statistics-items__filter-row">
                    <div className="statistics-items__filter-field">
                        <DateInputGradient
                            name="overallDateFrom"
                            placeholder="Дата с"
                            value={overallDateFrom}
                            onChange={(v: any) => setOverallDateFrom(v)}
                        />
                    </div>
                    <div className="statistics-items__filter-field">
                        <DateInputGradient
                            name="overallDateTo"
                            placeholder="Дата по"
                            value={overallDateTo}
                            onChange={(v: any) => setOverallDateTo(v)}
                        />
                    </div>
                    <div className="statistics-items__filter-field">
                        <Select
                            name="transferMetric"
                            placeholder="Выберите тип суммы"
                            items={transferMetricOptions.map((opt: any) => ({
                                value: opt.value,
                                name: String(opt.label || opt.name || "Без названия")
                            }))}
                            defaultValue={transferMetric}
                            onChange={(value: string) => setTransferMetric(value as TransferMetricKey)}
                        />
                    </div>

                    <button type="button" className="statistics-items__transfer-tab" onClick={onReset}>
                        Сбросить
                    </button>
                </div>
            </div>

            <div className="statistics-items__list statistics-items__list-big">
                {isOverallRangeMode ? (
                    <>
                        {renderTransferOverallRange(transferMetric)}
                        {renderTransferByCityRange(transferMetric)}
                        {renderTransferByCurrencyRange(transferMetric)}
                    </>
                ) : (
                    <>
                        {renderTransferOverallDefault()}
                        {renderTransferByCityDefault(transferMetric)}
                        {renderTransferByCurrencyDefault(transferMetric)}
                    </>
                )}
            </div>
        </>
    );
};

export default TransferOverallTab;